import { FC, ReactNode, createContext, useContext, useState } from "react";

// mui
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

type LoadingContextType = {
  loading: boolean;
  loadingWrapper: (func: () => Promise<void>) => void;
  setLoading: (loading: boolean) => void;
};

const LoadingContext = createContext<LoadingContextType>({
  loading: false,

  loadingWrapper: () => {},

  setLoading: () => {},
});

export const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState(false);

  const loadingWrapper = async (func: () => Promise<void>) => {
    try {
      setLoading(true);
      await func();
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadingContext.Provider
      value={{
        loading,
        loadingWrapper,
        setLoading,
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => useContext(LoadingContext);

export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * loadingWrapper 内で window.location.href 等によるページ遷移を行う場合、
 * 遷移完了前に finally が発火して loading が false に戻るのを防ぐために使用する。
 * ページアンロード時に JS コンテキストごと破棄されるため、永続的な pending は問題にならない。
 *
 * @example
 * await loadingWrapper(async () => {
 *   await saveData();
 *   await sleep(1500);
 *   window.location.href = "/next-page";
 *   await blockUntilPageUnload();
 * });
 */
export const blockUntilPageUnload = () => new Promise<never>(() => {});

export const Loading: FC = () => {
  const { loading } = useLoading();
  return (
    <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }} open={loading}>
      <CircularProgress color="inherit" />
    </Backdrop>
  );
};
